import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import type { Plan } from '../../shared/api/types';
import { Logo } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatPrice } from '../billing/format';
import { Icon } from '../shell/nav';
import { useSession } from '../session/SessionProvider';
import './landing.css';

const FEATURES = [
  { icon: 'user', title: 'Personas y familias', text: 'Miembros, familias e invitados en un solo lugar.' },
  { icon: 'calendar', title: 'Cultos y eventos', text: 'Calendario, turnos de servicio y asistencia.' },
  { icon: 'megaphone', title: 'Avisos', text: 'Cada aviso le llega a quien le corresponde.' },
  { icon: 'bible', title: 'Biblia y oración', text: 'Devocionales, planes de lectura y peticiones.' },
  { icon: 'baby', title: 'Guardería segura', text: 'Entrega con código y solo a personas autorizadas.' },
  { icon: 'coin', title: 'Finanzas claras', text: 'Ofrendas, fondos y reportes sin hojas de cálculo.' },
];

const VALUES = [
  { name: 'Fe', text: 'Cristo en el centro de todo lo que hacemos.' },
  { name: 'Comunidad', text: 'Una iglesia unida que se conoce y se cuida.' },
  { name: 'Servicio', text: 'Cada don puesto al servicio de los demás.' },
  { name: 'Crecimiento', text: 'Discípulos que crecen en la Palabra y en la oración.' },
  { name: 'Confianza', text: 'Los datos de tu gente, cuidados con respeto.' },
];

const STEPS = [
  { title: 'Crea tu cuenta', text: 'Con tu correo, en un minuto.' },
  { title: 'Registra tu iglesia', text: 'Empiezas con 15 días gratis.' },
  { title: 'Invita a tu gente', text: 'Cada quien entra desde su celular.' },
];

/** Landing pública: qué es KEORA, cómo empezar y planes. */
export function LandingPage() {
  const { state } = useSession();
  const signedIn = state.status === 'authenticated';
  const plans = useAsync((signal) => api.get<{ items: Plan[] }>('/saas/plans', signal).then((r) => r.items), []);
  const registerChurch = signedIn ? '/app/iglesias/nueva' : '/registro?siguiente=iglesia';

  return (
    <div className="landing">
      <header className="landing__bar">
        <Logo />
        <nav className="landing__nav" aria-label="Acceso">
          {signedIn ? (
            <Link className="btn btn--primary" to="/app">
              Ir a mi inicio
            </Link>
          ) : (
            <>
              <Link className="btn btn--ghost" to="/entrar">
                Iniciar sesión
              </Link>
              <Link className="btn btn--primary" to="/registro">
                Crear cuenta
              </Link>
            </>
          )}
        </nav>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-hero__text">
            <Link className="btn btn--primary btn--large lp-hero__find" to="/iglesias">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
              Buscar mi iglesia
            </Link>
            <p className="lp-kicker">Para iglesias</p>
            <h1>Tu iglesia, organizada y cerca de su gente.</h1>
            <p className="lp-hero__lead">Miembros, cultos, avisos y finanzas en una sola app, desde el celular.</p>
            <div className="landing__cta">
              <Link className="btn btn--primary btn--large" to={registerChurch}>
                Registrar mi iglesia
              </Link>
              <Link className="btn btn--secondary btn--large" to="/iglesias">
                Soy miembro
              </Link>
            </div>
            <p className="lp-hero__note">15 días gratis. Sin tarjeta para empezar.</p>
          </div>
          <PhonePreview />
        </section>

        <section className="lp-verse" aria-labelledby="lp-verse">
          <Mark />
          <figure>
            <h2 id="lp-verse" className="visually-hidden">Nuestro versículo</h2>
            <blockquote className="verse__text">
              «Y perseveraban en la doctrina de los apóstoles, y en la comunión, y en el partimiento del pan, y en las oraciones.»
            </blockquote>
            <figcaption className="verse__ref">Hechos 2:42 · RV 1909</figcaption>
          </figure>
        </section>

        <section className="lp-section" aria-labelledby="lp-values">
          <p className="eyebrow">Lo que nos mueve</p>
          <h2 id="lp-values">Hecho para servir a la iglesia</h2>
          <ul className="lp-values">
            {VALUES.map((v) => (
              <li key={v.name} className="lp-value">
                <h3>{v.name}</h3>
                <p>{v.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="lp-section" aria-labelledby="lp-features">
          <p className="eyebrow">Para iglesias</p>
          <h2 id="lp-features">Todo lo que tu iglesia necesita</h2>
          <ul className="lp-features">
            {FEATURES.map((f) => (
              <li key={f.title} className="lp-feature">
                <span className="lp-feature__icon">
                  <Icon name={f.icon} />
                </span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="lp-section" aria-labelledby="lp-steps">
          <h2 id="lp-steps">Empieza en tres pasos</h2>
          <ol className="lp-steps">
            {STEPS.map((s, i) => (
              <li key={s.title} className="lp-step">
                <span className="lp-step__n" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="lp-section" aria-labelledby="lp-plans">
          <p className="eyebrow">Registrar mi iglesia</p>
          <h2 id="lp-plans">Planes</h2>
          {plans.status === 'ready' && plans.data.length > 0 ? (
            <ul className="lp-plans">
              {plans.data.map((plan) => (
                <li key={plan.code} className="lp-plan">
                  <span className="badge">15 días gratis</span>
                  <h3>{plan.name}</h3>
                  <p className="lp-plan__price">
                    {formatPrice(plan.monthlyPriceCents, plan.currency)} <span>al mes</span>
                  </p>
                  {plan.description ? <p className="muted">{plan.description}</p> : null}
                  <Link className="btn btn--primary" to={registerChurch}>
                    Empezar prueba
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Los planes y precios se publicarán aquí próximamente.</p>
          )}
        </section>

        <section className="lp-trust" aria-label="Privacidad">
          <Icon name="check" />
          <p>
            <strong>Cada iglesia ve solo lo suyo.</strong> Los datos de niños y de cuidado pastoral están protegidos y cada acceso
            queda registrado.
          </p>
        </section>
      </main>

      <footer className="landing__footer">
        <span>© KEORA</span> · <Link to="/privacidad">Aviso de privacidad</Link> · <Link to="/terminos">Términos y condiciones</Link>
      </footer>
    </div>
  );
}

/** Símbolo de la marca en grande, como ilustración sobria (decorativo). */
function Mark() {
  return (
    <svg className="lp-mark" viewBox="29 11 57 83" aria-hidden="true">
      <path d="M44 13V92M31 34H57" stroke="currentColor" strokeWidth="3" fill="none" />
      <path d="M50 56Q58 30 84 24Q78 50 50 56Z" fill="currentColor" />
      <path d="M50 56Q78 62 84 90Q58 86 50 56Z" fill="currentColor" fillOpacity="0.65" />
    </svg>
  );
}

/** Vista de ejemplo de la app en un celular (decorativa). */
function PhonePreview() {
  return (
    <div className="lp-phone" aria-hidden="true">
      <div className="lp-phone__screen">
        <p className="lp-phone__hello">Hola, Ana</p>
        <div className="lp-card lp-card--brand">
          <span className="lp-card__label">Domingo 10:00</span>
          <strong>Culto de celebración</strong>
          <span className="lp-card__meta">Te toca: Bienvenida</span>
        </div>
        <div className="lp-card">
          <span className="lp-card__icon">
            <Icon name="megaphone" />
          </span>
          <div>
            <strong>Retiro de jóvenes</strong>
            <span className="lp-card__meta">Inscripciones abiertas</span>
          </div>
        </div>
        <div className="lp-card">
          <span className="lp-card__icon">
            <Icon name="pray" />
          </span>
          <div>
            <strong>Oración</strong>
            <span className="lp-card__meta">12 personas orando contigo</span>
          </div>
        </div>
        <div className="lp-card">
          <span className="lp-card__icon">
            <Icon name="bible" />
          </span>
          <div>
            <strong>Devocional de hoy</strong>
            <span className="lp-card__meta">Salmo 23</span>
          </div>
        </div>
        <div className="lp-phone__nav">
          <span><Icon name="home" /></span>
          <span><Icon name="calendar" /></span>
          <span><Icon name="inbox" /></span>
          <span><Icon name="user" /></span>
        </div>
      </div>
    </div>
  );
}
